import { storage } from "./storage";
import { getCsrfHeaders } from "@/lib/apiHeaders";

/**
 * Contact entry from Google People API
 */
export interface GoogleContact {
  email: string;
  name?: string;
  photo?: string;
  source: "directory" | "contacts" | "other_contacts";
}

/**
 * Cache for contacts to avoid repeated API calls
 */
interface ContactsCache {
  contacts: GoogleContact[];
  timestamp: number;
  expiresIn: number; // in milliseconds
}

// Cache for access token to avoid repeated backend calls
let cachedAccessToken: string | null = null;
let tokenCacheExpiry: number = 0;

const CACHE_KEY = "google_contacts_cache";
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes - longer cache to reduce API calls

/**
 * Get access token from backend
 */
const getAccessToken = async (): Promise<string | null> => {
  // Check if we have a valid cached token
  if (cachedAccessToken && Date.now() < tokenCacheExpiry) {
    return cachedAccessToken;
  }

  try {
    const response = await fetch("/api/auth/gmail-token", {
      credentials: "include",
      headers: getCsrfHeaders(),
    });

    if (!response.ok) {
      console.warn(
        "[Contacts] Failed to get access token from backend:",
        response.status,
      );
      cachedAccessToken = null;
      return null;
    }

    const data = await response.json();
    if (data.access_token) {
      cachedAccessToken = data.access_token;
      // Cache for slightly less than the actual expiry to be safe
      tokenCacheExpiry = Date.now() + ((data.expires_in || 3600) - 60) * 1000;
      return data.access_token;
    }

    return null;
  } catch (error) {
    console.error("[Contacts] Error fetching access token:", error);
    return null;
  }
};

/**
 * Get cached contacts if still valid
 */
const getCachedContacts = (): GoogleContact[] | null => {
  try {
    const cached = storage.getItem(CACHE_KEY);
    if (!cached) return null;

    const cacheData: ContactsCache = JSON.parse(cached);
    if (Date.now() - cacheData.timestamp > cacheData.expiresIn) {
      storage.removeItem(CACHE_KEY);
      return null;
    }

    return cacheData.contacts;
  } catch {
    return null;
  }
};

/**
 * Save contacts to cache
 */
const setCachedContacts = (contacts: GoogleContact[]) => {
  const cacheData: ContactsCache = {
    contacts,
    timestamp: Date.now(),
    expiresIn: CACHE_DURATION,
  };
  storage.setItem(CACHE_KEY, JSON.stringify(cacheData));
};

const searchDirectoryContacts = async (
  accessToken: string,
  query: string,
): Promise<GoogleContact[]> => {
  if (!query || query.length < 2) {
    return [];
  }

  try {
    console.log("[Contacts] Searching directory for:", query);
    const response = await fetch(
      "https://people.googleapis.com/v1/people:searchDirectoryPeople?" +
        new URLSearchParams({
          readMask: "names,emailAddresses,photos",
          sources: "DIRECTORY_SOURCE_TYPE_DOMAIN_PROFILE",
          pageSize: "20",
          query: query,
        }),
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.warn(
        "[Contacts] Directory API error:",
        response.status,
        errorText,
      );
      if (response.status === 429) {
        console.warn(
          "[Contacts] Rate limit exceeded for Directory API - using cached contacts only",
        );
        return [];
      }
      if (response.status === 403 || response.status === 401) {
        console.error(
          "[Contacts] ⚠️ Missing Google Workspace directory.readonly scope!",
        );
        console.error(
          "[Contacts] Organization contacts will not be available.",
        );
        console.error(
          "[Contacts] User needs to sign out and sign back in to grant proper permissions.",
        );
      }
      return [];
    }

    const data = await response.json();
    console.log(
      "[Contacts] Directory API returned",
      data.people?.length || 0,
      "contacts",
    );
    const contacts: GoogleContact[] = [];

    if (data.people) {
      for (const person of data.people) {
        const email = person.emailAddresses?.[0]?.value;
        if (email) {
          contacts.push({
            email,
            name: person.names?.[0]?.displayName,
            photo: person.photos?.[0]?.url,
            source: "directory",
          });
        }
      }
    }

    return contacts;
  } catch (error) {
    console.warn("[Contacts] Error searching directory:", error);
    return [];
  }
};

/**
 * Fetch "Other contacts" - people you've interacted with via Gmail
 */
const fetchOtherContacts = async (
  accessToken: string,
): Promise<GoogleContact[]> => {
  try {
    console.log("[Contacts] Fetching other contacts...");
    const response = await fetch(
      "https://people.googleapis.com/v1/otherContacts?" +
        new URLSearchParams({
          readMask: "names,emailAddresses,photos",
          pageSize: "100",
        }),
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.warn(
        "[Contacts] Other Contacts API error:",
        response.status,
        errorText,
      );
      if (response.status === 429) {
        console.warn(
          "[Contacts] Rate limit exceeded for Other Contacts API - returning cached contacts",
        );
        // Return cached contacts if available instead of empty array
        const cached = getCachedContacts();
        return cached || [];
      }
      // If 403/401, the user needs to re-authenticate with new scopes
      if (response.status === 403 || response.status === 401) {
        console.warn(
          "[Contacts] Missing contacts.other.readonly scope - user needs to re-authenticate",
        );
      }
      return [];
    }

    const data = await response.json();
    console.log(
      "[Contacts] Other Contacts API returned",
      data.otherContacts?.length || 0,
      "contacts",
    );
    const contacts: GoogleContact[] = [];

    if (data.otherContacts) {
      for (const person of data.otherContacts) {
        const email = person.emailAddresses?.[0]?.value;
        if (email) {
          contacts.push({
            email,
            name: person.names?.[0]?.displayName,
            photo: person.photos?.[0]?.url,
            source: "other_contacts",
          });
        }
      }
    }

    return contacts;
  } catch (error) {
    console.warn("[Contacts] Error fetching other contacts:", error);
    return [];
  }
};

/**
 * Search all contacts from available sources
 */
const searchAllContacts = async (query: string): Promise<GoogleContact[]> => {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    console.warn(
      "[Contacts] No access token available for contacts - user needs to sign in",
    );
    return [];
  }
  console.log(
    "[Contacts] Access token obtained, searching contacts for:",
    query,
  );

  // Try to use cached contacts first to reduce API calls
  const cachedContacts = getCachedContacts();

  // Fetch directory contacts (live search) and other contacts (use cache if available)
  const [directoryContacts, otherContacts] = await Promise.all([
    searchDirectoryContacts(accessToken, query),
    cachedContacts
      ? Promise.resolve(cachedContacts)
      : fetchOtherContacts(accessToken),
  ]);

  console.log(
    "[Contacts] Total fetched - Directory:",
    directoryContacts.length,
    "Other:",
    otherContacts.length,
    cachedContacts ? "(cached)" : "(live)",
  );

  // Combine and deduplicate by email (prefer directory entries)
  const emailMap = new Map<string, GoogleContact>();

  // Add other contacts first
  for (const contact of otherContacts) {
    emailMap.set(contact.email.toLowerCase(), contact);
  }

  // Override with directory contacts (higher priority)
  for (const contact of directoryContacts) {
    emailMap.set(contact.email.toLowerCase(), contact);
  }

  const contacts = Array.from(emailMap.values());

  // Sort by source priority (directory first) then by name
  contacts.sort((a, b) => {
    if (a.source === "directory" && b.source !== "directory") return -1;
    if (b.source === "directory" && a.source !== "directory") return 1;
    return (a.name || a.email).localeCompare(b.name || b.email);
  });

  return contacts;
};

/**
 * Get cached "other contacts" (people you've emailed)
 */
const getOtherContacts = async (): Promise<GoogleContact[]> => {
  const cached = getCachedContacts();
  if (cached) {
    return cached;
  }

  const accessToken = await getAccessToken();
  if (!accessToken) {
    return [];
  }

  const contacts = await fetchOtherContacts(accessToken);
  if (contacts.length > 0) {
    setCachedContacts(contacts);
  }

  return contacts;
};

/**
 * Search contacts by query string
 * Searches both directory (live) and cached other contacts
 */
const searchContacts = async (query: string): Promise<GoogleContact[]> => {
  if (!query.trim() || query.length < 2) {
    return [];
  }

  // Get all contacts matching the query
  const allContacts = await searchAllContacts(query);

  // Filter by query (for other contacts which are fetched in full)
  const lowerQuery = query.toLowerCase();

  return allContacts
    .filter(
      (contact) =>
        contact.email.toLowerCase().includes(lowerQuery) ||
        contact.name?.toLowerCase().includes(lowerQuery),
    )
    .slice(0, 20); // Limit results
};

/**
 * Clear the contacts cache
 */
const clearCache = () => {
  storage.removeItem(CACHE_KEY);
};

export const contactsService = {
  getOtherContacts,
  searchContacts,
  clearCache,
};
